import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Share as ShareIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { paiseToRupees } from '../../Utils/currencyConverter';

export interface TripData {
  id: string;
  title: string;
  location: string;
  duration: string;
  price: string;
  imageUrl: string;
  category: string;
  type: 'Trip' | 'Itinerary';
  status?: string;
  bookings?: number;
  date?: string;
  shareUrl?: string;
}

interface TripCardProps {
  trip: any
  variant: 'explore' | 'trips' | 'bookings' | 'reservations' | 'transaction';
  onEdit?: (trip: TripData) => void;
  onShare?: (trip: TripData) => void;
  onClick?: (trip: TripData) => void;
}

const TripCard: React.FC<TripCardProps> = ({
  trip,
  variant,
  onEdit,
  onShare,
  onClick,
}) => {
  const [shareAnchorEl, setShareAnchorEl] = useState<null | HTMLElement>(null);
  const [imageError, setImageError] = useState(false);

  // Reset image error when trip changes
  useEffect(() => {
    setImageError(false);
  }, [trip?._id || trip?.id, trip?.files?.[0]?.fileURL, trip?.imageUrl]);

  const handleCardClick = () => {
    if (onClick) {
      onClick(trip);
    }
  };

  const handleShareClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
  };

  const getShareUrl = () => {
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://ifinds.in' 
      : window.location.origin.replace('https://', 'http://');
    const tripId = trip?._id || trip?.id;
    const tripType = trip?.type?.toLowerCase() === 'itinerary' ? 'itinerary' : 'trip';
    
    if (tripId) {
      return `${baseUrl}/trips/${tripId}${tripType === 'itinerary' ? '?type=itinerary' : ''}`;
    }
    if (trip?.shareUrl) return trip.shareUrl;
    if (typeof window !== 'undefined') return window.location.href;
    return '';
  };

  const handleCopyLink = async (event: React.MouseEvent) => {
    event.stopPropagation();
    const shareUrl = getShareUrl();
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      onShare?.(trip);
    } catch (error) {
      console.error('Failed to copy link', error);
    } finally {
      handleShareClose();
    }
  };

  const handleWhatsAppShare = (event: React.MouseEvent) => {
    event.stopPropagation();
    const shareUrl = encodeURIComponent(getShareUrl());
    if (shareUrl && typeof window !== 'undefined') {
      window.open(`https://wa.me/?text=${shareUrl}`, '_blank', 'noopener,noreferrer');
      onShare?.(trip);
    }
    handleShareClose();
  };

  const handleEmailShare = (event: React.MouseEvent) => {
    event.stopPropagation();
    const shareUrl = getShareUrl();
    if (shareUrl && typeof window !== 'undefined') {
      window.location.href = `mailto:?subject=${encodeURIComponent(trip?.title ?? 'Check out this trip')}&body=${encodeURIComponent(shareUrl)}`;
      onShare?.(trip);
    }
    handleShareClose();
  };

  // Get image URL safely with fallback
  const getImageUrl = () => {
    if (variant === 'explore') {
      return trip?.files?.[0]?.fileURL || trip?.imageUrl || '';
    }
    return trip?.imageUrl || trip?.files?.[0]?.fileURL || '';
  };

  const imageUrl = getImageUrl();
  const hasImage = !imageError && imageUrl;

  // Explore variant - Grid card layout
  if (variant === 'explore') {
    return (
      <Box
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
        }}
        onClick={handleCardClick}
      >
        <Box sx={{ position: 'relative' }}>
          {hasImage ? (
            <CardMedia
              component="img"
              image={imageUrl}
              alt={trip?.title || 'Trip image'}
              onError={() => setImageError(true)}
              sx={{
                borderRadius: '20px',
                width: '100%',
                height: { xs: '200px', sm: '250px', md: '300px', lg: '350px' },
                objectFit: 'cover',
                maxWidth: '100%',
              }}
            />
          ) : (
            <Box
              sx={{
                borderRadius: '20px',
                width: '100%',
                height: { xs: '200px', sm: '250px', md: '300px', lg: '350px' },
                backgroundColor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                border: '1px solid #e0e0e0',
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: '#d0d0d0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ color: '#999', fontSize: '24px' }}>📷</Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#999',
                  fontSize: '14px',
                  fontWeight: 500,
                  textAlign: 'center',
                  px: 2,
                }}
              >
                No image found
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bgcolor: '#FFE0B2',
              color: '#8B4513',
              borderRadius: '0 20px 0 16px',
              px: 3,
              py: 1,
              fontSize: '14px',
              fontWeight: 500,
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            }}
          >
            {trip?.type ? `${trip.type.charAt(0).toUpperCase()}${trip.type.slice(1)}` : ''}

          </Box>
          <Box
            sx={{
              position: 'absolute',
              right: 10,
              bottom: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Button
              variant="outlined"
              sx={{
                minWidth: 0,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.18)',
                color: '#49454F',
                borderRadius: '24px',
                height: 48,
                px: 3,
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 8px 32px rgba(31, 38, 135, 0.37)',
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.35)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px rgba(31, 38, 135, 0.45)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Typography sx={{ color: '#49454F', fontWeight: 600, fontSize: '14px' }}>
                {trip?.user?.username || trip?.user?.name || 'User'}
              </Typography>
            </Button>
            <IconButton
              onClick={handleShareClick}
              sx={{
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.18)',
                color: '#49454F',
                borderRadius: '24px',
                width: 48,
                height: 48,
                boxShadow: '0 8px 32px rgba(31, 38, 135, 0.37)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.35)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px rgba(31, 38, 135, 0.45)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <ShareIcon />
            </IconButton>
            <Menu
              anchorEl={shareAnchorEl}
              open={Boolean(shareAnchorEl)}
              onClose={handleShareClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  mt: 1,
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(12px)',
                  minWidth: 180,
                },
              }}
            >
              <MenuItem onClick={handleCopyLink}>Copy link</MenuItem>
              <MenuItem onClick={handleWhatsAppShare}>Share via WhatsApp</MenuItem>
              <MenuItem onClick={handleEmailShare}>Share via Email</MenuItem>
            </Menu>
          </Box>
        </Box>
        <CardContent sx={{ p: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5, fontSize: '16px' }}>
            {trip.title}
          </Typography>
          <Typography variant="body2" color="#008387" sx={{ mb: 1 }}>
            {trip?.days} days • ₹{paiseToRupees(trip?.pricePerPerson).toFixed(2)}/person
          </Typography>
        </CardContent>
      </Box>
    );
  }

  // Trips variant - List layout with actions
  if (variant === 'trips') {
    const tripsImageUrl = trip?.imageUrl || trip?.files?.[0]?.fileURL || '';
    const hasTripsImage = !imageError && tripsImageUrl;

    return (
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: 1,
          mb: 2,
          overflow: 'hidden',
          position: 'relative',
        }}
        onClick={handleCardClick}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
          {hasTripsImage ? (
            <CardMedia
              component="img"
              sx={{ width: 100, height: 100, borderRadius: 2, objectFit: 'cover' }}
              image={tripsImageUrl}
              alt={trip?.title || 'Trip image'}
              onError={() => setImageError(true)}
            />
          ) : (
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: 2,
                backgroundColor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #e0e0e0',
                flexShrink: 0,
              }}
            >
              <Typography sx={{ color: '#999', fontSize: '20px', mb: 0.5 }}>📷</Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#999',
                  fontSize: '10px',
                  textAlign: 'center',
                  px: 1,
                }}
              >
                No image
              </Typography>
            </Box>
          )}
          <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" >
                {trip.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {trip.duration} • {trip.price}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
              {trip.status === 'pending' || trip.status === 'rejected' ? (
                <Box
                  sx={{
                    background: 'linear-gradient(90deg, #b31217 0%, #e52d27 100%)',
                    color: '#fff',
                    borderRadius: 2,
                    px: 3,
                    py: 0.5,
                    fontSize: '14px',
                    fontWeight: 500,
                    alignSelf: 'flex-start',
                  }}
                >
                  {trip.status === 'pending' ? 'Pending' : 'Rejected'}
                </Box>
              ) : (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare?.(trip);
                  }}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    width: 40,
                    height: 40,
                  }}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              )}
              {trip.status !== 'approved' && onEdit && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(trip);
                  }}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    width: 28,
                    height: 28,
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bgcolor: '#FFE0B2',
            color: '#8B4513',
            borderRadius: '0 20px 0 16px',
            px: 3,
            py: 1,
            fontSize: '14px',
            fontWeight: 500,
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
          }}
        >
          {trip?.type[0].toUpperCase() + trip?.type?.slice(1)}
        </Box>
      </Card>
    );
  }


  return null;
};

export default TripCard; 